import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, ArrowLeft, Loader2, Mail, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSiteContent } from '../hooks/useSiteContent';

import { BACKEND_URL } from '../config/api';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const { get } = useSiteContent();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { email });
      setSent(true);
      toast.success('Reset link sent!');
      // Start 60-second cooldown for resend
      setResendCooldown(60);
      const interval = setInterval(() => {
        setResendCooldown(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to submit reset request';
      // Check for rate limit
      if (error.response?.status === 429) {
        toast.error('Too many requests. Please wait a moment before trying again.');
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setLoading(true);
    
    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { email });
      toast.success('Reset link sent again!');
      setResendCooldown(60);
      const interval = setInterval(() => {
        setResendCooldown(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error('Too many requests. Please wait before trying again.');
      } else {
        toast.error('Failed to resend reset link');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-xl shadow-lg p-8 border border-slate-200">
          <button
            onClick={() => navigate('/login')}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Login
          </button>

          <div className="flex items-center justify-center gap-2 mb-8">
            <Building2 className="h-8 w-8 text-orange-500" />
            <h1 className="text-2xl font-bold text-slate-900">OnsiteHand</h1>
          </div>

          {!sent ? (
            <>
              <h2 className="text-2xl font-bold text-slate-900 mb-2 text-center">{get('auth.forgot.title', 'Forgot Password?')}</h2>
              <p className="text-slate-600 text-center mb-6">
                {get('auth.forgot.subtitle', "Enter your email and we'll send you a reset link.")}
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
                    {get('auth.login.email_label', 'Email')}
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="you@example.com"
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    data-testid="email-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="h-5 w-5" />
                      {get('auth.forgot.submit_btn', 'Send Reset Link')}
                    </>
                  )}
                </button>
              </form>
            </>
          ) : (
            <div className="text-center" data-testid="success-message">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">{get('auth.forgot.check_email_title', 'Check Your Email')}</h2>
              <p className="text-slate-600 mb-4">
                {get('auth.forgot.check_email_desc', "We've sent a password reset link to your email.")}
              </p>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
                <div className="flex gap-3">
                  <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">{get('auth.forgot.didnt_receive', "Didn't receive the email?")}</p>
                    <ul className="list-disc list-inside space-y-1 text-blue-700">
                      <li>{get('auth.forgot.check_spam', 'Check your spam or junk folder')}</li>
                      <li>Make sure you entered the correct email</li>
                      <li>{get('auth.forgot.link_expires', 'The link expires in 30 minutes')}</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                <button
                  onClick={handleResend}
                  disabled={loading || resendCooldown > 0}
                  className="btn-secondary w-full flex items-center justify-center gap-2"
                  data-testid="resend-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Sending...
                    </>
                  ) : resendCooldown > 0 ? (
                    `Resend available in ${resendCooldown}s`
                  ) : (
                    <>
                      <Mail className="h-5 w-5" />
                      Resend Email
                    </>
                  )}
                </button>
                
                <button
                  onClick={() => navigate('/login')}
                  className="btn-outline w-full"
                  data-testid="goto-login-btn"
                >
                  Back to Login
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
